import { type UserResource, UsersController_list_v1 } from '@repo/api-client';
import { useEffect, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';

/**
 * Who recorded something, by actor id — a price's setter, a billing machine's
 * attachment or product-code mapping.
 *
 * History names an **actor**, not a user: the id is taken from the
 * authenticated principal and is what history is keyed on, and `UserResource`
 * carries the `actorId` that joins the two.
 *
 * The staff list is owner-only on the API, so for a manager this read is
 * refused and every row keeps the bare id — the fallback each caller renders.
 *
 * `includeDeactivated` is on and is not an oversight. A price set by somebody
 * who has since left is still a price they set, and the default omits them —
 * without this, every row set by a departed manager would fall back to a bare
 * id, which is the case where knowing the name matters most.
 *
 * **A failure here costs a name, never the screen.** The ledger is the record
 * and the amounts are the record's point; a staff list that could not be read
 * leaves each row showing the id it already has, which is what the fallback in
 * `PriceRow` is for. It is read whole because it is bounded by the park and
 * because every row on the page needs it at once.
 */
export function useActorNames(): ReadonlyMap<string, UserResource> {
  const client = useApiClient();
  const [actors, setActors] = useState<ReadonlyMap<string, UserResource>>(new Map());

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    collectPages<UserResource>((offset, limit) =>
      client.request(UsersController_list_v1, {
        query: { offset, limit, includeDeactivated: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setActors(new Map(all.map((person) => [person.actorId, person])));
      })
      .catch(() => {
        if (live) setActors(new Map());
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  return actors;
}
